import React, { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";
import { DeliveryZone } from "../../utils/deliveryQuote";
import { DELIVERY_ZONE_TEXTS } from "../shared/constants";

interface Props {
  open: boolean;
  /** Groupings already in use, so a new municipality joins one instead of inventing a synonym. */
  zoneOptions: string[];
  isCreating: boolean;
  onCreate: (zone: DeliveryZone) => void;
  onCancel: () => void;
}

const ID_PATTERN = /^[a-z0-9-]+$/;

const isNonNegative = (value: string) => value.trim() !== "" && Number(value) >= 0;

/**
 * A dialog rather than the create/edit page pair the banners and items have.
 *
 * Those two need a page because they upload an image. A zone is eight scalar fields typed once per
 * municipality — 53 of them arrive from the seed endpoint, and this exists for the fifty-fourth.
 */
const CreateZoneDialog: React.FC<Props> = ({
  open,
  zoneOptions,
  isCreating,
  onCreate,
  onCancel,
}) => {
  const [id, setId] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [zone, setZone] = useState("");
  const [fee, setFee] = useState("0");
  const [threshold, setThreshold] = useState("");
  const [etaMin, setEtaMin] = useState("1");
  const [etaMax, setEtaMax] = useState("2");
  const [displayOrder, setDisplayOrder] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!id.trim() || !municipality.trim()) return setError(DELIVERY_ZONE_TEXTS.requiredFields);
    if (!ID_PATTERN.test(id.trim())) return setError(DELIVERY_ZONE_TEXTS.invalidId);
    if (!isNonNegative(fee) || !isNonNegative(etaMin) || !isNonNegative(etaMax)) {
      return setError(DELIVERY_ZONE_TEXTS.invalidNumbers);
    }
    // An empty threshold is `null` — "no umbral here" — and `0` is "always free". The server
    // distinguishes them too, so this cannot collapse to a truthiness check.
    if (threshold.trim() !== "" && !isNonNegative(threshold)) {
      return setError(DELIVERY_ZONE_TEXTS.invalidNumbers);
    }
    if (Number(etaMin) > Number(etaMax)) return setError(DELIVERY_ZONE_TEXTS.invalidEtaOrder);

    setError("");
    onCreate({
      id: id.trim(),
      municipality: municipality.trim(),
      department: "Antioquia",
      zone: zone.trim(),
      fee_cop: Number(fee),
      free_shipping_threshold_cop: threshold.trim() === "" ? null : Number(threshold),
      eta_days_min: Number(etaMin),
      eta_days_max: Number(etaMax),
      is_active: isActive,
      display_order: displayOrder.trim() === "" ? 0 : Number(displayOrder),
    });
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm" data-testid="create-zone-dialog">
      <DialogTitle>{DELIVERY_ZONE_TEXTS.createTitle}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label={DELIVERY_ZONE_TEXTS.idLabel}
            helperText={DELIVERY_ZONE_TEXTS.idHelp}
            value={id}
            onChange={(e) => setId(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label={DELIVERY_ZONE_TEXTS.municipalityLabel}
            value={municipality}
            onChange={(e) => setMunicipality(e.target.value)}
            size="small"
            fullWidth
          />
          <Autocomplete
            freeSolo
            options={zoneOptions}
            value={zone}
            onInputChange={(_e, value) => setZone(value ?? "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={DELIVERY_ZONE_TEXTS.zoneLabel}
                helperText={DELIVERY_ZONE_TEXTS.zoneHelp}
                size="small"
              />
            )}
          />
          <Box display="flex" gap={2}>
            <TextField
              label={DELIVERY_ZONE_TEXTS.feeLabel}
              type="number"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ min: 0 }}
            />
            <TextField
              label={DELIVERY_ZONE_TEXTS.thresholdLabel}
              helperText={DELIVERY_ZONE_TEXTS.thresholdHelp}
              type="number"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Box>
          <Box display="flex" gap={2}>
            <TextField
              label={DELIVERY_ZONE_TEXTS.etaMinLabel}
              helperText={DELIVERY_ZONE_TEXTS.etaHelp}
              type="number"
              value={etaMin}
              onChange={(e) => setEtaMin(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ min: 0 }}
            />
            <TextField
              label={DELIVERY_ZONE_TEXTS.etaMaxLabel}
              type="number"
              value={etaMax}
              onChange={(e) => setEtaMax(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Box>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              label={DELIVERY_ZONE_TEXTS.orderLabel}
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              size="small"
            />
            <FormControlLabel
              control={
                <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              }
              label={DELIVERY_ZONE_TEXTS.activeLabel}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isCreating}>
          {DELIVERY_ZONE_TEXTS.cancelButton}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isCreating}>
          {isCreating ? DELIVERY_ZONE_TEXTS.creating : DELIVERY_ZONE_TEXTS.createSubmit}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateZoneDialog;
